/**
 * Drawing Mixin for Konva-based components
 * Provides shared functionality for Apple Pencil detection, drawing, and touch handling
 */

export default {
  data() {
    return {
      // Drawing State
      lines: [],
      currentDrawing: null,
      isDrawing: false,
      eraserMode: false,
      linesEdited: false,
      
      // Drawing Properties
      drawingProps: {
        stroke: "#000000",
        strokeWidth: 2,
        lineCap: "round",
        lineJoin: "round",
        tension: 0.5,
        globalCompositeOperation: "source-over",
      },

      // Point Reduction Settings - Optimized for performance
      pointReductionConfig: {
        minPointDistance: 4, // Original value - maximum drawing smoothness
        rdpTolerance: 3.5,   // Original value - preserves fine detail
        maxPointsPerLine: 300, // Original value - allows detailed drawing
        enableRealTimeFiltering: true, // Enable distance-based filtering during drawing
        enableAsyncSimplification: true, // Enable non-blocking simplification
        simplificationThreshold: 500, // Points before async simplification kicks in
      },

      // Eraser Settings
      eraserConfig: {
        eraserRadius: 15,    // Eraser collision radius (pixels)
        maxEraserPoints: 20, // Maximum eraser trail points to keep
        intersectionTolerance: 2, // Tolerance for intersection detection (pixels)
        enableDistanceCheck: true, // Enable distance-based erasing as fallback
      },

      // Performance optimization state
      performanceState: {
        pendingSimplifications: new Set(),
        lastOptimizationTime: 0,
        frameRequestId: null,
      },

    };
  },

  mounted() {
    this.initializeDrawing();
    
    // Expose debug methods globally for easy console access
    if (typeof window !== 'undefined') {
      window.debugDrawing = {
        state: () => this.debugDrawingState(),
        validate: () => this.validateDrawingState(),
        reset: () => this.resetDrawingState(),
        checkEvents: () => this.checkEventResponsiveness(),
        lines: () => this.lines,
        component: this
      };
    }
  },

  beforeDestroy() {
    this.cleanupDrawing();
  },

  methods: {
    // === INITIALIZATION ===
    initializeDrawing() {
      // Allow components to override point reduction settings
      this.initializePointReductionSettings();
      
      // Ensure array synchronization on initialization
      this.ensureArraySynchronization();
    },

    initializePointReductionSettings() {
      // Components can override this method to customize point reduction
      // Default settings are already defined in data()
    },

    updatePointReductionSettings(newSettings) {
      // Allow runtime updates to point reduction settings
      this.pointReductionConfig = {
        ...this.pointReductionConfig,
        ...newSettings
      };
    },

    updateEraserSettings(newSettings) {
      // Allow runtime updates to eraser settings
      this.eraserConfig = {
        ...this.eraserConfig,
        ...newSettings
      };
    },

    // === CONFIGURATION ===
    getPointReductionConfig() {
      // Allow dynamic configuration based on drawing context
      return {
        ...this.pointReductionConfig,
        // Adjust settings based on stroke width (thicker strokes can use more aggressive reduction)
        rdpTolerance: Math.max(this.pointReductionConfig.rdpTolerance, this.drawingProps.strokeWidth * 0.75),
        // Adjust min distance based on zoom level if available
        minPointDistance: this.$zoom ? this.pointReductionConfig.minPointDistance / this.$zoom : this.pointReductionConfig.minPointDistance,
      };
    },

    cleanupDrawing() {
      // Cancel any pending performance optimizations
      if (this.performanceState?.frameRequestId) {
        cancelAnimationFrame(this.performanceState.frameRequestId);
        this.performanceState.frameRequestId = null;
      }
      
      // Clear pending simplifications
      if (this.performanceState?.pendingSimplifications) {
        this.performanceState.pendingSimplifications.clear();
      }
    },


    // === APPLE PENCIL DETECTION ===
    detectApplePencilFromTouch(e) {
      // Simplified Apple Pencil detection using touch event properties
      if (!e.evt || !e.evt.touches || e.evt.touches.length === 0) {
        return false;
      }

      const touch = e.evt.touches[0];
      const isApplePencil = touch.touchType === 'stylus' || touch.force > 0;
      
      // Reliable Apple Pencil detection methods
      return isApplePencil;
    },

    // === EVENT HANDLING ===
    handleMouseDown(e, pageIndex) {
      // Always allow mouse drawing
      if (this.eraserMode) {
        this.startObjEraser(e, pageIndex);
      } else {
        this.startDrawing(e, pageIndex);
      }
    },

    handleTouchStart(e, pageIndex) {
      // Track that our handler was called
      
      // Track the last time we received a touch event
      if (typeof window !== 'undefined') {
        window.lastTouchEventTime = Date.now();
      }
      
      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // Invalid pageIndex
        return;
      }

      // Check if it's Apple Pencil per-event
      const isApplePencil = this.detectApplePencilFromTouch(e);
      
      
      if (isApplePencil) {
        // Always prevent scrolling for Apple Pencil
        e.evt.preventDefault();
        if (this.eraserMode) {
          this.startObjEraser(e, pageIndex);
        } else {
          this.startDrawing(e, pageIndex);
        }
      }
      // For finger touches, don't prevent default to allow scrolling
      // Eraser mode only works with Apple Pencil, finger touches are ignored
    },

    handleMouseMove(e, pageIndex) {
      // Always handle mouse move events when drawing
      if (this.isDrawing) {
        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
    },

    handleTouchMove(e, pageIndex) {
      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('[ERROR_CONDITION] handleTouchMove: Invalid pageIndex', pageIndex);
        return;
      }

      // Check if it's Apple Pencil
      const isApplePencil = this.detectApplePencilFromTouch(e);
      
      
      // Handle touch move for Apple Pencil only
      if (this.isDrawing && isApplePencil) {
        // Always prevent scrolling when drawing with Apple Pencil
        e.evt.preventDefault();
        
        if (this.eraserMode) {
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        } else {
          this.continueDrawing(e, pageIndex);
        }
      }
      // For finger touches, don't prevent default to allow scrolling
      // Eraser mode only works with Apple Pencil, finger touches are ignored
    },

    handleMouseUp(e, pageIndex) {
      // Always handle mouse up events when drawing
      if (this.isDrawing) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('handleMouseUp: Invalid pageIndex', pageIndex);
          return;
        }

        if (this.eraserMode) {
          this.objErasing(pageIndex);
          // Ensure lines array and page exist before accessing
          if (this.lines && Array.isArray(this.lines) && this.lines[pageIndex] && Array.isArray(this.lines[pageIndex]) && this.lines[pageIndex].length > 0) {
            this.lines[pageIndex].pop();
          }
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
      }
    },

    handleTouchEnd(e, pageIndex) {
      
      // Track the last time we received a touch event
      if (typeof window !== 'undefined') {
        window.lastTouchEventTime = Date.now();
      }
      
      // Only handle touch end for Apple Pencil (detected per-event)
      if (this.isDrawing && this.detectApplePencilFromTouch(e)) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('handleTouchEnd: Invalid pageIndex', pageIndex);
          return;
        }

        if (this.eraserMode) {
          this.objErasing(pageIndex);
          // Ensure lines array and page exist before accessing
          if (this.lines && Array.isArray(this.lines) && this.lines[pageIndex] && Array.isArray(this.lines[pageIndex]) && this.lines[pageIndex].length > 0) {
            this.lines[pageIndex].pop();
          }
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
      }
    },

    handleMouseLeave(e, pageIndex) {
      // Reset drawing state when mouse leaves the canvas area
      if (this.isDrawing) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('handleMouseLeave: Invalid pageIndex', pageIndex);
          // Still reset drawing state even with invalid pageIndex
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          this.objErasing(pageIndex);
          // Remove the temporary eraser line
          if (this.lines && Array.isArray(this.lines) && this.lines[pageIndex] && Array.isArray(this.lines[pageIndex]) && this.lines[pageIndex].length > 0) {
            this.lines[pageIndex].pop();
          }
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
        // Force reset drawing state
        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    handleTouchLeave(e, pageIndex) {
      // Reset drawing state when touch leaves the canvas area (Apple Pencil only)
      if (this.isDrawing && this.detectApplePencilFromTouch(e)) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('handleTouchLeave: Invalid pageIndex', pageIndex);
          // Still reset drawing state even with invalid pageIndex
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          this.objErasing(pageIndex);
          // Remove the temporary eraser line
          if (this.lines && Array.isArray(this.lines) && this.lines[pageIndex] && Array.isArray(this.lines[pageIndex]) && this.lines[pageIndex].length > 0) {
            this.lines[pageIndex].pop();
          }
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
        // Force reset drawing state
        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    handlePointerLeave(e, pageIndex) {
      // Universal handler for pointer leave events
      if (this.isDrawing) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('handlePointerLeave: Invalid pageIndex', pageIndex);
          // Still reset drawing state even with invalid pageIndex
          this.isDrawing = false;
          this.currentDrawing = null;
          return;
        }

        if (this.eraserMode) {
          this.objErasing(pageIndex);
          // Remove the temporary eraser line
          if (this.lines && Array.isArray(this.lines) && this.lines[pageIndex] && Array.isArray(this.lines[pageIndex]) && this.lines[pageIndex].length > 0) {
            this.lines[pageIndex].pop();
          }
          if (this.linesEdited) {
            // Add history tracking if available
            if (this.addHistory) this.addHistory();
            this.linesEdited = false;
          }
        } else {
          this.finishDrawing();
        }
        // Force reset drawing state
        this.isDrawing = false;
        this.currentDrawing = null;
      }
    },

    // === DRAWING METHODS ===
    startDrawing(e, pageIndex) {
      
      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('[ERROR_CONDITION] startDrawing: Invalid pageIndex', pageIndex);
        return;
      }

      // Validate event and stage
      if (!e || !e.target || typeof e.target.getStage !== 'function') {
        // console.warn('[ERROR_CONDITION] startDrawing: Invalid event or target', e);
        return;
      }

      const stage = e.target.getStage();
      if (!stage || typeof stage.getPointerPosition !== 'function') {
        // console.warn('[ERROR_CONDITION] startDrawing: Invalid stage', stage);
        return;
      }

      const pos = stage.getPointerPosition();
      if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
        // console.warn('[ERROR_CONDITION] startDrawing: Invalid pointer position', pos);
        return;
      }

      // Check for potential state conflicts
      if (this.isDrawing) {
        // State conflict detected - startDrawing called while already drawing
      }

      this.isDrawing = true;
      this.currentDrawing = {
        ...this.drawingProps,
        points: [this.roundCoordinate(pos.x), this.roundCoordinate(pos.y)],
      };


      // Ensure lines array exists and is properly initialized
      if (!this.lines || !Array.isArray(this.lines)) {
        this.lines = [];
      }

      // Ensure lines array exists for this page
      if (!this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex])) {
        this.$set(this.lines, pageIndex, []);
      }

      this.lines[pageIndex].push(this.currentDrawing);
      
    },

    continueDrawing(e, pageIndex) {
      
      // Validate state before proceeding
      this.validateDrawingState();
      
      if (!this.isDrawing || !this.currentDrawing) {
        // continueDrawing called with invalid state
        return;
      }

      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('continueDrawing: Invalid pageIndex', pageIndex);
        return;
      }

      // Validate event and stage
      if (!e || !e.target || typeof e.target.getStage !== 'function') {
        // console.warn('continueDrawing: Invalid event or target', e);
        return;
      }

      const stage = e.target.getStage();
      if (!stage || typeof stage.getPointerPosition !== 'function') {
        // console.warn('continueDrawing: Invalid stage', stage);
        return;
      }

      const pos = stage.getPointerPosition();
      if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
        // console.warn('continueDrawing: Invalid pointer position', pos);
        return;
      }

      // Validate current drawing points
      if (!this.currentDrawing.points || !Array.isArray(this.currentDrawing.points)) {
        // console.warn('continueDrawing: Invalid current drawing points', this.currentDrawing);
        return;
      }

      // Ensure lines array and page exist - with defensive initialization
      if (!this.lines || !Array.isArray(this.lines)) {
        // console.warn('continueDrawing: Invalid lines array, reinitializing', this.lines);
        this.lines = [];
      }
      
      // Reinitialize missing page array instead of failing
      if (!this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex])) {
        // console.warn('continueDrawing: Missing or invalid page array, reinitializing for pageIndex', pageIndex);
        this.$set(this.lines, pageIndex, []);
        
        // If we lost the current drawing, we need to recreate it
        if (this.currentDrawing && this.isDrawing) {
          this.lines[pageIndex].push(this.currentDrawing);
        }
      }

      const newX = this.roundCoordinate(pos.x);
      const newY = this.roundCoordinate(pos.y);

      // Get last point for distance filtering
      const points = this.currentDrawing.points;
      const lastX = points.length >= 2 ? points[points.length - 2] : undefined;
      const lastY = points.length >= 1 ? points[points.length - 1] : undefined;

      // Only add point if it's far enough from the last point
      if (this.shouldAddPoint(newX, newY, lastX, lastY)) {
        // Add new point to current line with rounded coordinates
        this.currentDrawing.points = this.currentDrawing.points.concat([newX, newY]);

        // Apply simplification only when line becomes very long - now non-blocking
        const config = this.getPointReductionConfig();
        if (this.currentDrawing.points.length > config.simplificationThreshold * 2) {
          if (config.enableAsyncSimplification) {
            this.scheduleAsyncSimplification();
          } else {
            this.simplifyCurrentLine();
          }
        }

        // Update the line in the array with enhanced bounds checking
        const lineIndex = this.lines[pageIndex].length - 1;
        if (lineIndex >= 0 && lineIndex < this.lines[pageIndex].length && this.lines[pageIndex][lineIndex]) {
          this.$set(this.lines[pageIndex], lineIndex, { ...this.currentDrawing });
        } else {
          // Fallback: if line index is invalid, re-add the current drawing
          // Invalid line index, re-adding current drawing
          this.lines[pageIndex].push({ ...this.currentDrawing });
        }
      }
    },

    finishDrawing() {
      
      
      if (!this.isDrawing || !this.currentDrawing) {
        // finishDrawing called with invalid state
        return;
      }

      // Simplify line for better performance using configurable tolerance
      if (this.currentDrawing.points.length > 4) {
        const config = this.getPointReductionConfig();
        const points = this.chunkArrayInGroups(this.currentDrawing.points, 2);
        const simplifiedPoints = this.simplifyLineRDP(points, config.rdpTolerance);
        this.currentDrawing.points = [].concat.apply([], simplifiedPoints);
        
      } else {
        // Round coordinates even for short lines
        const roundedPoints = [];
        for (let i = 0; i < this.currentDrawing.points.length; i += 2) {
          roundedPoints.push(
            this.roundCoordinate(this.currentDrawing.points[i]),
            this.roundCoordinate(this.currentDrawing.points[i + 1])
          );
        }
        this.currentDrawing.points = roundedPoints;
        
      }


      
      this.isDrawing = false;
      this.currentDrawing = null;
      
    },

    // === DRAWING UTILITIES ===
    async clearAllDrawings() {
      // This method should be overridden by components to handle their specific clearing logic
      this.lines = [];
    },

    addHistory() {
      // This method should be implemented by components that need history tracking
      // Default implementation does nothing
    },

    initializeLinesForPage(pageIndex) {
      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('initializeLinesForPage: Invalid pageIndex', pageIndex);
        return;
      }

      // Ensure lines array exists and is properly initialized
      if (!this.lines || !Array.isArray(this.lines)) {
        this.lines = [];
      }

      if (!this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex])) {
        this.$set(this.lines, pageIndex, []);
      }
    },

    // === STATE SYNCHRONIZATION ===
    ensureArraySynchronization() {
      // Ensure lines and pages arrays are properly aligned
      if (!this.lines || !Array.isArray(this.lines)) {
        this.lines = [];
      }
      
      // If pages exist but lines don't, initialize lines
      if (this.pages && Array.isArray(this.pages)) {
        for (let i = 0; i < this.pages.length; i++) {
          if (!this.lines[i] || !Array.isArray(this.lines[i])) {
            this.$set(this.lines, i, []);
          }
        }
      }
    },

    // === UTILITY METHODS ===
    getPointDistance(x1, y1, x2, y2) {
      return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    },

    shouldAddPoint(newX, newY, lastX, lastY) {
      const config = this.getPointReductionConfig();
      if (!config.enableRealTimeFiltering) return true;
      if (lastX === undefined || lastY === undefined) return true;
      
      const distance = this.getPointDistance(newX, newY, lastX, lastY);
      return distance >= config.minPointDistance;
    },

    simplifyCurrentLine() {
      if (!this.currentDrawing || this.currentDrawing.points.length <= 4) return;

      const config = this.getPointReductionConfig();
      const points = this.chunkArrayInGroups(this.currentDrawing.points, 2);
      const simplifiedPoints = this.simplifyLineRDP(points, config.rdpTolerance);
      this.currentDrawing.points = [].concat.apply([], simplifiedPoints);
    },

    /**
     * Schedule non-blocking line simplification using requestIdleCallback
     */
    scheduleAsyncSimplification() {
      if (!this.currentDrawing) return;
      
      const lineId = this.generateCurrentLineId();
      if (this.performanceState.pendingSimplifications.has(lineId)) {
        return; // Already scheduled
      }
      
      this.performanceState.pendingSimplifications.add(lineId);
      
      // Use requestIdleCallback for non-critical optimizations
      const callback = (deadline) => {
        try {
          if (deadline.timeRemaining() > 5 && this.currentDrawing) {
            this.simplifyCurrentLine();
          } else {
            // Reschedule if not enough time
            requestIdleCallback(callback, { timeout: 1000 });
            return;
          }
        } catch (error) {
          // console.warn('Async simplification error:', error);
        } finally {
          this.performanceState.pendingSimplifications.delete(lineId);
        }
      };
      
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(callback, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          callback({ timeRemaining: () => 10 });
        }, 16);
      }
    },

    /**
     * Generate a unique ID for the current drawing line
     */
    generateCurrentLineId() {
      return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    /**
     * Optimized batch drawing updates
     */
    scheduleBatchDraw() {
      if (this.performanceState.frameRequestId) {
        return; // Already scheduled
      }
      
      this.performanceState.frameRequestId = requestAnimationFrame(() => {
        try {
          // Find all stages and batch draw them
          Object.keys(this.$refs || {}).forEach(refKey => {
            if (refKey.startsWith('stage')) {
              const vueStage = this.$refs[refKey]?.[0];
              if (vueStage && vueStage.getStage) {
                const stage = vueStage.getStage();
                if (stage && typeof stage.batchDraw === 'function') {
                  stage.batchDraw();
                }
              }
            }
          });
        } catch (error) {
          // console.warn('Batch draw error:', error);
        } finally {
          this.performanceState.frameRequestId = null;
        }
      });
    },

    chunkArrayInGroups(arr, size) {
      if (!arr || !Array.isArray(arr)) return [];
      const result = [];
      for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
      }
      return result;
    },

    roundCoordinate(value) {
      // Round coordinates to 1 decimal place to reduce file size and improve performance
      return Math.round(value * 10) / 10;
    },

    // === PERFORMANCE OPTIMIZATIONS ===
    getEraserStats() {
      // Utility method to get eraser statistics for debugging
      return {
        config: { ...this.eraserConfig },
        currentEraserPoints: this.currentDrawing?.points?.length || 0,
        eraserMode: this.eraserMode,
        isDrawing: this.isDrawing,
      };
    },

    previewEraserDeletion(pageIndex) {
      // Debug method to preview what lines would be deleted without actually deleting them
      // Add parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('previewEraserDeletion: Invalid pageIndex', pageIndex);
        return [];
      }

      // Validate preconditions
      if (!this.lines || !Array.isArray(this.lines) || !this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex]) || !this.currentDrawing || !this.eraserMode) {
        return [];
      }

      const eraserPoints = this.currentDrawing.points;
      if (!eraserPoints || !Array.isArray(eraserPoints) || eraserPoints.length < 4) return [];

      const linesToDelete = [];
      
      for (let lineIndex = this.lines[pageIndex].length - 1; lineIndex >= 0; lineIndex--) {
        if (lineIndex === this.lines[pageIndex].length - 1) continue;

        const line = this.lines[pageIndex][lineIndex];
        if (!line || !line.points || !Array.isArray(line.points) || line.points.length < 4) continue;

        if (this.lineIntersectsWithEraser(line.points, eraserPoints)) {
          linesToDelete.push({
            index: lineIndex,
            line: line,
            pointCount: line.points.length / 2
          });
        }
      }

      return linesToDelete;
    },

    getLineStats() {
      // Utility method to get statistics about current lines for debugging
      const stats = {
        totalLines: 0,
        totalPoints: 0,
        averagePointsPerLine: 0,
        maxPointsInLine: 0,
      };

      this.lines.forEach(page => {
        if (Array.isArray(page)) {
          page.forEach(line => {
            if (line && line.points) {
              stats.totalLines++;
              const pointCount = line.points.length / 2;
              stats.totalPoints += pointCount;
              stats.maxPointsInLine = Math.max(stats.maxPointsInLine, pointCount);
            }
          });
        }
      });

      stats.averagePointsPerLine = stats.totalLines > 0 ? stats.totalPoints / stats.totalLines : 0;
      return stats;
    },

    roundPoint(point) {
      if (!point || point.length < 2) return point;
      return [
        this.roundCoordinate(point[0]),
        this.roundCoordinate(point[1])
      ];
    },

    simplifyLineRDP(points, tolerance) {
      if (!points || points.length < 2) return points;

      const simplify = (start, end) => {
        let maxDist = 0;
        let index = 0;

        const p1 = points[start];
        const p2 = points[end];

        for (let i = start + 1; i < end; i++) {
          const point = points[i];
          const dist = this.getPerpendicularDistance(point, p1, p2);

          if (dist > maxDist) {
            index = i;
            maxDist = dist;
          }
        }

        if (maxDist > tolerance) {
          const recResults1 = simplify(start, index);
          const recResults2 = simplify(index, end);

          return recResults1
            .slice(0, recResults1.length - 1)
            .concat(recResults2);
        } else {
          return [this.roundPoint(p1), this.roundPoint(p2)];
        }
      };

      const simplifiedPoints = simplify(0, points.length - 1);
      
      // Round all coordinates in the final result
      return simplifiedPoints.map(point => this.roundPoint(point));
    },

    getPerpendicularDistance(point, lineStart, lineEnd) {
      const dx = lineEnd[0] - lineStart[0];
      const dy = lineEnd[1] - lineStart[1];

      if (dx === 0 && dy === 0) {
        return Math.sqrt(
          Math.pow(point[0] - lineStart[0], 2) +
            Math.pow(point[1] - lineStart[1], 2)
        );
      }

      const t =
        ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) /
        (dx * dx + dy * dy);

      if (t < 0) {
        return Math.sqrt(
          Math.pow(point[0] - lineStart[0], 2) +
            Math.pow(point[1] - lineStart[1], 2)
        );
      } else if (t > 1) {
        return Math.sqrt(
          Math.pow(point[0] - lineEnd[0], 2) +
            Math.pow(point[1] - lineEnd[1], 2)
        );
      } else {
        const projX = lineStart[0] + t * dx;
        const projY = lineStart[1] + t * dy;
        return Math.sqrt(
          Math.pow(point[0] - projX, 2) + Math.pow(point[1] - projY, 2)
        );
      }
    },

    // === ERASING METHODS ===
    startObjEraser(e, pageIndex) {
      if (this.eraserMode) {
        // Add parameter validation
        if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
          // console.warn('startObjEraser: Invalid pageIndex', pageIndex);
          return;
        }

        // Validate event and stage
        if (!e || !e.target || typeof e.target.getStage !== 'function') {
          // console.warn('startObjEraser: Invalid event or target', e);
          return;
        }

        this.isDrawing = true;
        const stage = e.target.getStage();
        if (!stage || typeof stage.getPointerPosition !== 'function') {
          // console.warn('startObjEraser: Invalid stage', stage);
          return;
        }

        const pos = stage.getPointerPosition();
        if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
          // console.warn('startObjEraser: Invalid pointer position', pos);
          return;
        }

        this.currentDrawing = {
          ...this.drawingProps,
          stroke: "gainsboro",
          strokeWidth: 5,
          bezier: true,
          points: [this.roundCoordinate(pos.x), this.roundCoordinate(pos.y)],
        };

        // Ensure lines array exists and is properly initialized
        if (!this.lines || !Array.isArray(this.lines)) {
          this.lines = [];
        }

        // Ensure lines array exists for this page
        if (!this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex])) {
          this.$set(this.lines, pageIndex, []);
        }

        this.lines[pageIndex].push(this.currentDrawing);
      }
    },

    objErasing(pageIndex) {
      // Add comprehensive parameter validation
      if (pageIndex === undefined || pageIndex === null || !Number.isInteger(pageIndex) || pageIndex < 0) {
        // console.warn('objErasing: Invalid pageIndex', pageIndex);
        return;
      }

      // Ensure lines array exists and is initialized
      if (!this.lines || !Array.isArray(this.lines)) {
        this.lines = [];
      }

      // Initialize the page array if it doesn't exist
      if (!this.lines[pageIndex] || !Array.isArray(this.lines[pageIndex])) {
        this.$set(this.lines, pageIndex, []);
      }

      // Check if we have current drawing and it's valid
      if (!this.currentDrawing || !this.currentDrawing.points || !Array.isArray(this.currentDrawing.points)) {
        // console.warn('objErasing: No valid current drawing');
        return;
      }

      // Additional safety check for eraser mode
      if (!this.eraserMode) {
        // console.warn('objErasing: Called while not in eraser mode');
        return;
      }

      // Maintain limited eraser trail for better performance
      const maxPoints = this.eraserConfig.maxEraserPoints * 2; // *2 because points are [x,y] pairs
      if (this.currentDrawing.points.length > maxPoints) {
        this.currentDrawing.points.splice(0, this.currentDrawing.points.length - maxPoints);
      }

      // Get recent eraser points for intersection testing
      const eraserPoints = this.currentDrawing.points;
      if (eraserPoints.length < 4) return; // Need at least 2 points (4 coordinates)

      // Collect indices of lines to delete (in reverse order to avoid index shifting)
      const linesToDelete = [];
      
      // Iterate through lines in reverse order to safely delete
      for (let lineIndex = this.lines[pageIndex].length - 1; lineIndex >= 0; lineIndex--) {
        // Skip the current eraser line (it's the last one)
        if (lineIndex === this.lines[pageIndex].length - 1) continue;

        const line = this.lines[pageIndex][lineIndex];
        if (!line || !line.points || line.points.length < 4) continue;

        // Check if this line intersects with the eraser path
        if (this.lineIntersectsWithEraser(line.points, eraserPoints)) {
          linesToDelete.push(lineIndex);
          this.linesEdited = true;
        }
      }

      // Delete lines in reverse order (already collected in reverse)
      linesToDelete.forEach(index => {
        this.lines[pageIndex].splice(index, 1);
      });

      // Check if all lines on the page are erased
      if (this.lines[pageIndex] && this.lines[pageIndex].length <= 1) { // <= 1 because eraser line might still be there
        // Reset the page to empty array instead of removing it to preserve array indices
        // This prevents the "continueDrawing: Invalid lines array or page" error
        this.$set(this.lines, pageIndex, []);
        
        // Note: Do NOT remove pages from arrays as this causes index misalignment
        // between pages and lines arrays, leading to canvas disappearing errors
      }
    },

    // === ERASER INTERSECTION METHODS ===
    lineIntersectsWithEraser(linePoints, eraserPoints) {
      // Test if any line segment intersects with any eraser segment
      const lineSegments = this.getLineSegments(linePoints);
      const eraserSegments = this.getLineSegments(eraserPoints);

      // Test line-to-line intersections
      for (const lineSegment of lineSegments) {
        for (const eraserSegment of eraserSegments) {
          if (this.intersect(...lineSegment, ...eraserSegment)) {
            return true;
          }
        }
      }

      // Fallback: distance-based collision detection
      if (this.eraserConfig.enableDistanceCheck) {
        return this.lineIntersectsWithEraserRadius(linePoints, eraserPoints);
      }

      return false;
    },

    lineIntersectsWithEraserRadius(linePoints, eraserPoints) {
      // Check if any line point is within eraser radius of any eraser point
      const radius = this.eraserConfig.eraserRadius;
      const radiusSquared = radius * radius;

      for (let i = 0; i < linePoints.length; i += 2) {
        const lineX = linePoints[i];
        const lineY = linePoints[i + 1];

        for (let j = 0; j < eraserPoints.length; j += 2) {
          const eraserX = eraserPoints[j];
          const eraserY = eraserPoints[j + 1];

          const distanceSquared = (lineX - eraserX) ** 2 + (lineY - eraserY) ** 2;
          if (distanceSquared <= radiusSquared) {
            return true;
          }
        }
      }

      return false;
    },

    getLineSegments(points) {
      // Convert points array to line segments [[x1,y1,x2,y2], ...]
      const segments = [];
      for (let i = 0; i < points.length - 2; i += 2) {
        segments.push([
          points[i],     // x1
          points[i + 1], // y1
          points[i + 2], // x2
          points[i + 3]  // y2
        ]);
      }
      return segments;
    },

    intersect(x1, y1, x2, y2, x3, y3, x4, y4) {
      // Check for degenerate line segments
      if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false;
      }

      const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
      
      // Lines are parallel
      if (Math.abs(denominator) < 1e-10) {
        return false;
      }

      const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
      const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

      // Add tolerance for intersection detection
      const tolerance = this.eraserConfig.intersectionTolerance / 100; // Convert to normalized tolerance
      
      // Check if intersection occurs within line segments (with tolerance)
      if (ua >= -tolerance && ua <= 1 + tolerance && ub >= -tolerance && ub <= 1 + tolerance) {
        const x = x1 + ua * (x2 - x1);
        const y = y1 + ua * (y2 - y1);
        return { x, y };
      }

      return false;
    },

    // === DEBUG AND STATE MONITORING ===
    debugDrawingState() {
      const state = {
        isDrawing: this.isDrawing,
        eraserMode: this.eraserMode,
        currentDrawing: !!this.currentDrawing,
        currentDrawingPoints: this.currentDrawing ? this.currentDrawing.points.length : 0,
        linesEdited: this.linesEdited,
        totalLines: this.lines ? this.lines.length : 0,
        timestamp: Date.now(),
        lastTouchEventTime: typeof window !== 'undefined' ? window.lastTouchEventTime : null,
        timeSinceLastTouch: typeof window !== 'undefined' && window.lastTouchEventTime ? 
          Date.now() - window.lastTouchEventTime : null
      };
      
      return state;
    },

    
    checkEventResponsiveness() {
      const now = Date.now();
      const lastEventTime = typeof window !== 'undefined' ? window.lastTouchEventTime : null;
      const timeSinceLastEvent = lastEventTime ? now - lastEventTime : null;
      
      const status = {
        lastEventTime,
        timeSinceLastEvent,
        isResponsive: timeSinceLastEvent ? timeSinceLastEvent < 10000 : null, // 10 seconds threshold
        timestamp: now
      };
      
      return status;
    },
    
    validateDrawingState() {
      const issues = [];
      
      if (this.isDrawing && !this.currentDrawing) {
        issues.push('isDrawing=true but currentDrawing=null');
      }
      
      if (!this.isDrawing && this.currentDrawing) {
        issues.push('isDrawing=false but currentDrawing exists');
      }
      
      if (this.currentDrawing && (!this.currentDrawing.points || this.currentDrawing.points.length === 0)) {
        issues.push('currentDrawing exists but has no points');
      }
      
      // Check array synchronization
      if (this.pages && Array.isArray(this.pages) && this.lines && Array.isArray(this.lines)) {
        if (this.pages.length !== this.lines.length) {
          issues.push(`Array length mismatch: pages(${this.pages.length}) !== lines(${this.lines.length})`);
        }
        
        // Check for missing page arrays
        for (let i = 0; i < this.pages.length; i++) {
          if (!this.lines[i] || !Array.isArray(this.lines[i])) {
            issues.push(`Missing lines array for page ${i}`);
          }
        }
      }
      
      if (issues.length > 0) {
        // console.warn('[STATE_VALIDATION] Drawing state issues detected:', issues);
        // console.warn('[STATE_VALIDATION] Current state:', this.debugDrawingState());
        
        // Attempt automatic recovery for array synchronization issues
        this.ensureArraySynchronization();
      }
      
      return issues;
    },
    
    resetDrawingState() {
      
      this.isDrawing = false;
      this.currentDrawing = null;
      this.linesEdited = false;
      
    },

    // === STAGE CONFIGURATION ===
    getDrawingStageConfig(baseConfig = {}) {
      return {
        ...baseConfig,
        // Always enable stage listening for all input types
        listening: true,
      };
    },
  },
};